import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

interface SparklineProps {
  data: number[];
  labels?: string[];
  trendUp?: boolean;
  /** Optional explicit tone so a sideways (range) chart is not drawn as bullish. */
  tone?: 'up' | 'flat' | 'down';
  formatValue?: (value: number) => string;
}

interface SparkPoint {
  index: number;
  value: number;
  dateLabel?: string;
}

interface SparkTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: SparkPoint }>;
  formatValue?: (value: number) => string;
}

function SparkTooltip({ active, payload, formatValue }: SparkTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5">
      <p className="font-num text-sm font-semibold leading-none text-foreground-950">
        {formatValue ? formatValue(point.value) : point.value}
      </p>
      {point.dateLabel ? (
        <p className="mt-1 text-[11px] leading-none text-foreground-500">{point.dateLabel}</p>
      ) : null}
    </div>
  );
}

export default function Sparkline({ data, labels, trendUp = true, tone, formatValue }: SparklineProps) {
  const chartData: SparkPoint[] = data.map((value, index) => ({
    index,
    value,
    dateLabel: labels?.[index],
  }));
  const toneClass = tone ? `is-${tone}` : trendUp ? 'is-up' : 'is-down';

  return (
    <div className={`spark-area-chart h-full w-full ${toneClass}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
          <XAxis dataKey="index" hide />
          <Area
            type="monotone"
            dataKey="value"
            isAnimationActive
            animationDuration={1400}
            dot={false}
            activeDot={{ r: 3.5, className: 'spark-active-dot' }}
          />
          <Tooltip
            content={<SparkTooltip formatValue={formatValue} />}
            cursor={{ className: 'spark-cursor' }}
            allowEscapeViewBox={{ x: false, y: true }}
            offset={12}
            wrapperStyle={{ zIndex: 30, outline: 'none' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}